import { createBigDecimal, minus, plus, times } from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { CostBasis } from '../../types/costBasis'
import type { Transaction } from '../../types/transaction'
import { createAssetKey } from '../../utils'
import {
  getTotalPl,
  splitTradePlByNftHoldingPeriod,
} from '../utils/transactionTransferPl'
import type {
  CreateCategoryTransactionParameters,
  CreateCategoryTransactionReturnTypes,
} from './types'
import { getCostByAssetBalancesAndBorrows } from './util/getCostByAssetBalancesAndBorrows'
import { updateCostBy5PercentRule } from './util/updateCostBy5PercentRule'

type CreateTransferTransactionParameters =
  CreateCategoryTransactionParameters & {
    readonly transfer: Transfer
    readonly costBasis: CostBasis
    readonly handleTransferAsLoss?: boolean
    readonly use5PercentRule?: boolean
  }

type CreateTransferTransactionReturnTypes = CreateCategoryTransactionReturnTypes

export const createTransferTransaction = ({
  actionId,
  timestamp,
  transfer,
  assetBalances,
  assetBorrows,
  usedBalances,
  nftAcquiredTimestamps,
  costBasis,
  handleTransferAsLoss = false,
  use5PercentRule = false,
}: CreateTransferTransactionParameters): CreateTransferTransactionReturnTypes => {
  const key = createAssetKey(transfer.asset)
  const price = transfer.price ?? createBigDecimal(0n)
  const value = times(transfer.amount, price, 6)

  const {
    cost,
    assetBalances: keyAssetBalances,
    assetBorrows: keyAssetBorrows,
    usedBalance,
  } = transfer.asset.type === 'FiatCurrency'
    ? {
        cost: value,
        assetBalances: [],
        assetBorrows: [],
        usedBalance: {
          amount: createBigDecimal(0n),
          value: createBigDecimal(0n),
        },
      }
    : getCostByAssetBalancesAndBorrows({
        amount: transfer.amount,
        assetBalances: assetBalances.get(key) ?? [],
        assetBorrows: assetBorrows.get(key) ?? [],
        usedBalance: usedBalances.get(key) ?? {
          amount: createBigDecimal(0n),
          value: createBigDecimal(0n),
        },
        costBasis,
      })
  if (keyAssetBalances.length > 0) {
    assetBalances.set(key, keyAssetBalances)
  } else {
    assetBalances.delete(key)
  }
  if (keyAssetBorrows.length > 0) {
    assetBorrows.set(key, keyAssetBorrows)
  } else {
    assetBorrows.delete(key)
  }
  if (usedBalance.amount.value !== 0n) {
    usedBalances.set(key, usedBalance)
  } else {
    usedBalances.delete(key)
  }

  const costWith5PercentRule = use5PercentRule
    ? updateCostBy5PercentRule(cost, value)
    : cost
  const balance = (assetBalances.get(key) ?? []).reduce(
    (acc, assetBalance) => plus(acc, assetBalance.amount),
    createBigDecimal(0n),
  )
  const borrowing = (assetBorrows.get(key) ?? []).reduce(
    (acc, assetBorrow) => plus(acc, assetBorrow.amount),
    createBigDecimal(0n),
  )

  const classifiedTradePl = splitTradePlByNftHoldingPeriod({
    tradePl: minus(value, costWith5PercentRule),
    transfer: {
      amount: transfer.amount,
      asset: transfer.asset,
      price,
      zeroCompleted: transfer.price === undefined,
      cost,
      balance,
      borrowing,
      feePl: createBigDecimal(0n),
      incomePl: createBigDecimal(0n),
      tradePl: createBigDecimal(0n),
      totalPl: createBigDecimal(0n),
    },
    timestamp,
    nftAcquiredTimestamps,
  })
  const transaction: Transaction = {
    actionId,
    timestamp,
    order: 0,
    category: 'transfer',
    ins: [],
    outs: [
      {
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: transfer.price === undefined,
        cost,
        balance,
        borrowing,
        feePl: handleTransferAsLoss
          ? times(value, createBigDecimal(-1n))
          : createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: classifiedTradePl.tradePl,
        separateTradePl: createBigDecimal(0n),
        nftTradePl: classifiedTradePl.nftTradePl,
        nftCapitalPl: classifiedTradePl.nftCapitalPl,
        separateRealizedPl: createBigDecimal(0n),
        realizedPl: createBigDecimal(0n),
        airdropPl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      },
    ],
  }
  transaction.outs[0].totalPl = getTotalPl({ transfer: transaction.outs[0] })

  return {
    transaction,
    assetBalances,
    assetBorrows,
    usedBalances,
    nftAcquiredTimestamps,
  }
}
